import { useCallback, useEffect, useRef, useState } from 'react'

import { type Enum } from '@webapp/infrastructure/types/Enum'
import { runConversationTurn } from '../../business-logic/use-cases/RunConversationTurnUseCase'
import { type ConversationTelemetryEvent } from '../../value-objects/ConversationTelemetryEvent'
import {
  createConversationTurnItem,
  type ConversationTurnItem,
  type ConversationTurnItemConstructor,
} from '../../value-objects/ConversationTurnItem'
import { ConversationItemKinds } from '../../value-objects/enums'

export const ConversationStreamStatuses = {
  IDLE: 'idle',
  STREAMING: 'streaming',
  DONE: 'done',
  ERROR: 'error',
} as const

export type ConversationStreamStatusEnum = Enum<
  typeof ConversationStreamStatuses
>

export interface UseConversationStreamResult {
  items: ConversationTurnItem[]
  status: ConversationStreamStatusEnum
  thinkingLabel: Nullable<string>
  telemetry: ConversationTelemetryEvent[]
  sendMessage: (message: string) => boolean
  sendSilentMessage: (message: string) => boolean
}

export const useConversationStream = (
  initialQuery: string
): UseConversationStreamResult => {
  const [items, setItems] = useState<ConversationTurnItem[]>([])
  const [status, setStatus] = useState<ConversationStreamStatusEnum>(
    ConversationStreamStatuses.IDLE
  )
  const [telemetry, setTelemetry] = useState<ConversationTelemetryEvent[]>([])
  const [thinkingLabel, setThinkingLabel] = useState<Nullable<string>>(null)

  const nextIdRef = useRef(0)
  const disconnectRef = useRef<Nullable<() => void>>(null)
  const conversationIdRef = useRef<Nullable<string>>(null)
  const isStreamingRef = useRef(false)
  const hasSeededRef = useRef(false)

  const appendItem = useCallback(
    (constructor: ConversationTurnItemConstructor) => {
      setItems((prev) => [
        ...prev,
        createConversationTurnItem(constructor, nextIdRef.current++),
      ])
    },
    []
  )

  const upsertItem = useCallback(
    (constructor: ConversationTurnItemConstructor) => {
      if (constructor.kind !== ConversationItemKinds.ENVELOPE) {
        appendItem(constructor)
        return
      }
      setItems((prev) => {
        const index = prev.findIndex(
          (item) =>
            item.kind === ConversationItemKinds.ENVELOPE &&
            item.envelopeId === constructor.envelopeId
        )
        if (index === -1) {
          return [
            ...prev,
            createConversationTurnItem(constructor, nextIdRef.current++),
          ]
        }
        const next = prev.slice()
        next[index] = createConversationTurnItem(constructor, prev[index].id)
        return next
      })
    },
    [appendItem]
  )

  const markEnvelopesAnswered = useCallback(() => {
    setItems((prev) =>
      prev.map((item) =>
        item.kind === ConversationItemKinds.ENVELOPE && !item.isAnswered
          ? { ...item, isAnswered: true }
          : item
      )
    )
  }, [])

  const openTurn = useCallback(
    (message: string) => {
      setStatus(ConversationStreamStatuses.STREAMING)
      setThinkingLabel(null)
      isStreamingRef.current = true
      disconnectRef.current?.()
      disconnectRef.current = runConversationTurn(
        { message, conversationId: conversationIdRef.current },
        {
          onItem: upsertItem,
          onSessionStart: (conversationId) => {
            conversationIdRef.current = conversationId
          },
          onProgress: setThinkingLabel,
          onTelemetry: (event) => setTelemetry((prev) => [...prev, event]),
          onDone: () => {
            isStreamingRef.current = false
            setThinkingLabel(null)
            setStatus((prev) =>
              prev === ConversationStreamStatuses.ERROR
                ? prev
                : ConversationStreamStatuses.DONE
            )
          },
          onError: (error) => {
            isStreamingRef.current = false
            setThinkingLabel(null)
            appendItem({
              kind: ConversationItemKinds.ERROR,
              message: error.message ?? error.code,
            })
            setStatus(ConversationStreamStatuses.ERROR)
          },
        }
      )
    },
    [appendItem, upsertItem]
  )

  const sendMessage = useCallback(
    (rawMessage: string) => {
      if (isStreamingRef.current) return false

      const message = rawMessage.trim()
      if (message === '') return false

      markEnvelopesAnswered()
      appendItem({
        kind: ConversationItemKinds.MESSAGE,
        sender: 'user',
        content: message,
      })
      openTurn(message)
      return true
    },
    [appendItem, markEnvelopesAnswered, openTurn]
  )

  // Cart mutations post a turn to the agent without echoing a user bubble in
  // the transcript; the agent streams back an updated cart envelope.
  const sendSilentMessage = useCallback(
    (rawMessage: string) => {
      if (isStreamingRef.current) return false

      const message = rawMessage.trim()
      if (message === '') return false

      openTurn(message)
      return true
    },
    [openTurn]
  )

  useEffect(() => {
    if (!hasSeededRef.current) {
      hasSeededRef.current = true
      appendItem({
        kind: ConversationItemKinds.MESSAGE,
        sender: 'user',
        content: initialQuery,
      })
    }
    openTurn(initialQuery)
    return () => disconnectRef.current?.()
  }, [appendItem, initialQuery, openTurn])

  return {
    items,
    status,
    thinkingLabel,
    telemetry,
    sendMessage,
    sendSilentMessage,
  }
}
